import { EnvironmentId } from '@/react/portainer/environments/types';

import { useVulnerabilityReports } from '../../queries/useVulnerabilityReports';
import { TrivySeverity, Vulnerability } from '../../types';

export const MAX_TOOLTIP_FINDINGS = 5;

// Fetched on first hover: fix availability needs the full report, and loading
// that per visible row would trade one large request for a page of small ones.
export function FindingsSummary({
  environmentId,
  namespace,
  workload,
  severity,
  label,
  total,
  enabled,
}: {
  environmentId: EnvironmentId;
  namespace: string;
  workload: string;
  severity: TrivySeverity;
  label: string;
  total: number;
  enabled: boolean;
}) {
  const reportsQuery = useVulnerabilityReports({
    environmentId,
    namespace,
    resourceName: workload,
    enabled,
    select: (reports) => selectFindings(reports, severity),
  });

  if (reportsQuery.isLoading || !reportsQuery.isSuccess) {
    return <div className="text-left">Loading findings…</div>;
  }

  const findings = reportsQuery.data ?? [];
  const fixable = findings.filter((finding) => !!finding.fixedVersion).length;
  const shown = findings.slice(0, MAX_TOOLTIP_FINDINGS);
  const remaining = total - shown.length;

  return (
    <div className="text-left">
      <div className="mb-2 opacity-75">
        {fixable} of {findings.length} {label} findings have a fix
      </div>

      {shown.map((finding) => (
        <div
          key={`${finding.vulnerabilityID}-${finding.resource}`}
          className="mb-2 last:mb-0"
        >
          <div className="flex items-baseline gap-2">
            <span className="font-medium">{finding.vulnerabilityID}</span>
            {finding.score !== undefined && (
              <span className="tabular-nums opacity-75">
                {finding.score.toFixed(1)}
              </span>
            )}
          </div>
          <div className="opacity-75">
            <span className="break-all">{finding.resource}</span>
            {finding.fixedVersion ? (
              <span> &rarr; {finding.fixedVersion}</span>
            ) : (
              <span> &middot; no fix</span>
            )}
          </div>
        </div>
      ))}

      {remaining > 0 && (
        <div className="mt-2 opacity-75">+ {remaining} more</div>
      )}
    </div>
  );
}

// Worst score first; unscored findings sort last rather than being dropped.
export function selectFindings(
  reports: Array<{ report: { vulnerabilities?: Array<Vulnerability> } }>,
  severity: TrivySeverity
): Array<Vulnerability> {
  return reports
    .flatMap((report) => report.report.vulnerabilities ?? [])
    .filter((finding) => finding.severity === severity)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}
